import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useBaseNumbers } from '../api';

const PAGE_SIZE = 10;
const fmt = (n: number) => n.toLocaleString('en-IN');

interface Props {
  campaignId: number | string | null;
  onClose: () => void;
}

/** Uploaded base file viewer — search by number + pagination. */
export function BaseNumbersDialog({ campaignId, onClose }: Props) {
  const open = campaignId !== null;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // Fresh state every time a different campaign is opened.
  useEffect(() => {
    setPage(1);
    setSearch('');
    setQ('');
  }, [campaignId]);

  useEffect(() => {
    const next = search.replace(/\D/g, '');
    if (next === q) return;
    const t = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search, q]);

  const query = useBaseNumbers(campaignId ?? '', page, PAGE_SIZE, q, open);
  const data = query.data;
  const total = data?.total ?? 0;
  const matched = data?.matched ?? 0;
  const pages = Math.max(Math.ceil(matched / PAGE_SIZE), 1);
  const from = matched === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, matched);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Base numbers — campaign #{campaignId}</DialogTitle>
          <DialogDescription>
            {fmt(total)} number{total === 1 ? '' : 's'} in the uploaded base file.
          </DialogDescription>
        </DialogHeader>

        {data && !data.available ? (
          <p className="py-6 text-sm text-muted-foreground">
            The base file is not available to preview for this campaign.
          </p>
        ) : (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search mobile number…"
                inputMode="numeric"
                className="h-9 pl-9 pr-9"
                aria-label="Search base numbers"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="min-h-[23.5rem]">
              {query.isLoading ? (
                <p className="py-6 text-sm text-muted-foreground">Loading…</p>
              ) : query.isError ? (
                <p className="py-6 text-sm text-destructive">Could not load base numbers.</p>
              ) : (data?.rows ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {q ? `No number matches “${q}”.` : 'No numbers.'}
                </p>
              ) : (
                <div
                  className={`max-h-[60vh] overflow-auto rounded-md border border-border transition-opacity duration-150 ${
                    query.isFetching ? 'opacity-60' : 'opacity-100'
                  }`}
                >
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-muted">
                      <tr>
                        <th className="w-14 px-3 py-2 text-left font-medium">#</th>
                        {(data?.headers ?? []).map((h, i) => (
                          <th key={`${h}-${i}`} className="px-3 py-2 text-left font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.rows ?? []).map((r, i) => (
                        <tr key={from + i} className="border-t border-border">
                          <td className="px-3 py-1.5 text-muted-foreground">{from + i}</td>
                          {r.map((c, j) => (
                            <td key={j} className="break-all px-3 py-1.5 font-mono text-xs">
                              {c}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                Showing {fmt(from)}–{fmt(to)} of {fmt(matched)}
                {q && matched !== total ? ` (filtered from ${fmt(total)})` : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || query.isFetching}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="whitespace-nowrap text-sm text-muted-foreground">
                  Page {fmt(page)} / {fmt(pages)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= pages || query.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}